import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-and-drop reordering + nesting of cards.
//
// REORDER: drag vertically between cards (SortableJS handles this)
// NEST: while dragging, every other card shows a folder-tab drop zone.
//       Cards above show a bottom tab, cards below show a top tab.
//       When the drag ghost overlaps a tab zone, it highlights.
//       On drop, if a zone is highlighted, a dependency is created.
//       Dragging a nested card to a new position breaks its dependency.

export default class extends Controller {
  static values = { url: String, projectId: Number }

  connect() {
    this._nestTarget = null
    this._draggedFrame = null
    this._zones = []
    this._mouseY = 0
    this._boundCheckZones = this._checkZoneOverlap.bind(this)
    this._boundTrackMouse = (e) => { this._mouseY = e.clientY || e.touches?.[0]?.clientY || 0 }

    this._sortable = Sortable.create(this.element, {
      handle:         "[data-sort-handle]",
      draggable:      "turbo-frame",
      filter:         "[data-empty-state], [data-kanban-empty]",
      animation:      150,
      ghostClass:     "opacity-40",
      chosenClass:    "shadow-lg",
      direction:      "vertical",
      forceFallback:  true,
      fallbackOnBody: false,
      swapThreshold:  0.8,
      onStart:  (evt) => this._onStart(evt),
      onEnd:    (evt) => this._onEnd(evt)
    })
  }

  disconnect() {
    this._sortable?.destroy()
    this._sortable = null
    this._cleanup()
  }

  _onStart(evt) {
    this._draggedFrame = evt.item
    this._nestTarget = null
    this._createNestZones()

    // Track mouse position and poll for zone overlap
    document.addEventListener("mousemove", this._boundTrackMouse)
    document.addEventListener("touchmove", this._boundTrackMouse)
    this._pollInterval = setInterval(this._boundCheckZones, 50)
  }

  _onEnd(evt) {
    clearInterval(this._pollInterval)

    const draggedFrame = evt.item
    const draggedCardEl = draggedFrame?.querySelector("[data-card-id]")
    const nestTarget = this._nestTarget

    this._cleanup()

    if (nestTarget && draggedCardEl) {
      const cardId = draggedCardEl.dataset.cardId
      if (cardId !== nestTarget) {
        this._createDependency(cardId, nestTarget, draggedFrame)
      }
    } else if (draggedCardEl?.dataset.dependsOnCardId) {
      // Nested card moved normally -- break the link
      const cardId = draggedCardEl.dataset.cardId
      const dependsOnCardId = draggedCardEl.dataset.dependsOnCardId
      this._removeDependency(cardId, dependsOnCardId, draggedFrame)
    } else {
      this._persistOrder()
    }

    this._draggedFrame = null
  }

  // --- Nest zone management ---

  _createNestZones() {
    const draggedCardId = this._draggedFrame?.querySelector("[data-card-id]")?.dataset.cardId
    const frames = Array.from(this.element.querySelectorAll(":scope > turbo-frame"))
    const dragIdx = frames.indexOf(this._draggedFrame)

    this._zones = []

    frames.forEach((frame, idx) => {
      if (frame === this._draggedFrame) return
      const cardEl = frame.querySelector("[data-card-id]")
      if (!cardEl) return

      // Skip the dragged card (forceFallback may keep original in DOM)
      if (cardEl.dataset.cardId === draggedCardId) return

      // Skip if this card depends on the dragged card (would be circular)
      if (cardEl.dataset.dependsOnCardId === draggedCardId) return

      // Skip if this card is already nested (nesting under it would create 3 levels)
      if (cardEl.dataset.dependsOnCardId) return

      // Add zones on both edges -- overlap detection picks the closest
      const topZone = this._buildZone(cardEl, "top")
      const bottomZone = this._buildZone(cardEl, "bottom")
      this._zones.push({ element: topZone, cardId: cardEl.dataset.cardId })
      this._zones.push({ element: bottomZone, cardId: cardEl.dataset.cardId })
    })
  }

  _buildZone(cardEl, position) {
    const zone = document.createElement("div")
    zone.dataset.nestZone = "true"

    const isTop = position === "top"
    zone.className = [
      "absolute right-2 w-32 h-7 z-20",
      "flex items-center justify-center",
      "rounded-md",
      "border-2 border-dashed border-blue-300 bg-blue-50",
      "pointer-events-none",
      "transition-all duration-100",
      isTop ? "-top-4" : "-bottom-4"
    ].join(" ")

    zone.innerHTML = `
      <div class="flex items-center gap-1 text-blue-400 text-[10px] font-semibold uppercase tracking-wider">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
        </svg>
        Nest
      </div>
    `

    cardEl.style.position = "relative"
    cardEl.style.overflow = "visible"
    // Ensure turbo-frame and container don't clip the zone
    const turboFrame = cardEl.closest("turbo-frame")
    if (turboFrame) turboFrame.style.overflow = "visible"
    cardEl.appendChild(zone)
    return zone
  }

  _checkZoneOverlap() {
    const mouseY = this._mouseY
    if (!mouseY) return

    let closest = null
    let closestDist = Infinity

    for (const { element: zone, cardId } of this._zones) {
      const zoneRect = zone.getBoundingClientRect()
      // Check if mouse is within or near the zone vertically (generous 12px buffer)
      const inZone = mouseY >= zoneRect.top - 12 && mouseY <= zoneRect.bottom + 12
      const dist = Math.abs(mouseY - (zoneRect.top + zoneRect.height / 2))

      if (inZone && dist < closestDist) {
        closest = { zone, cardId }
        closestDist = dist
      }
    }

    // Reset all zones to default state
    for (const { element: zone } of this._zones) {
      zone.classList.remove("border-blue-500", "bg-blue-400", "border-solid", "text-white", "scale-105", "shadow-md")
      zone.classList.add("border-blue-300", "bg-blue-50", "border-dashed")
      const label = zone.querySelector("div")
      if (label) { label.classList.remove("text-white"); label.classList.add("text-blue-400") }
    }

    if (closest) {
      this._nestTarget = closest.cardId
      closest.zone.classList.remove("border-blue-300", "bg-blue-50", "border-dashed", "text-blue-400")
      closest.zone.classList.add("border-blue-500", "bg-blue-400", "border-solid", "text-white", "scale-105", "shadow-md")
      const label = closest.zone.querySelector("div")
      if (label) label.classList.remove("text-blue-400")
      if (label) label.classList.add("text-white")
    } else {
      this._nestTarget = null
    }
  }

  _cleanup() {
    clearInterval(this._pollInterval)
    document.removeEventListener("mousemove", this._boundTrackMouse)
    document.removeEventListener("touchmove", this._boundTrackMouse)
    this._nestTarget = null
    this._zones = []

    // Remove all nest zones
    this.element.querySelectorAll("[data-nest-zone]").forEach(z => z.remove())

    // Reset overflow on cards
    this.element.querySelectorAll("[data-card-id]").forEach(el => {
      el.style.overflow = ""
    })
  }

  // --- Dependency CRUD ---

  async _createDependency(cardId, dependsOnCardId, frame) {
    const projectId = this.projectIdValue
    if (!projectId) return

    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(`/projects/${projectId}/cards/${cardId}/card_dependencies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify({ depends_on_card_id: dependsOnCardId })
      })

      if (response.ok) {
        const el = frame.querySelector("[data-card-id]")
        if (el) {
          el.classList.add("ml-8", "border-l-4", "border-l-blue-300")
          el.dataset.dependsOnCardId = dependsOnCardId
        }

        // Add parent border
        const parentEl = this.element.querySelector(`[data-card-id="${dependsOnCardId}"]`)
        if (parentEl) {
          parentEl.classList.add("border-l-4", "border-l-blue-400")
          parentEl.dataset.hasDependents = ""
        }

        // Position the frame right after the parent
        const parentFrame = parentEl?.closest("turbo-frame")
        if (parentFrame && parentFrame.nextSibling !== frame) {
          parentFrame.after(frame)
        }

        this._persistOrder()
      } else {
        // Show error as flash
        try {
          const data = await response.json()
          const msg = data.errors?.join(", ") || "Could not nest card"
          this._showFlash(msg)
        } catch {
          this._showFlash("Could not nest card")
        }
      }
    } catch { /* silent */ }
  }

  async _removeDependency(cardId, dependsOnCardId, frame) {
    const projectId = this.projectIdValue
    if (!projectId) return

    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(`/projects/${projectId}/cards/${cardId}/card_dependencies/unnest?depends_on_card_id=${dependsOnCardId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const el = frame.querySelector("[data-card-id]")
        if (el) {
          el.classList.remove("ml-8", "border-l-4", "border-l-blue-300")
          delete el.dataset.dependsOnCardId
        }

        // Remove parent's blue border if it has no more dependents
        const parentEl = this.element.querySelector(`[data-card-id="${dependsOnCardId}"]`)
        if (parentEl) {
          const stillHasDeps = this.element.querySelector(`[data-depends-on-card-id="${dependsOnCardId}"]`)
          if (!stillHasDeps) {
            parentEl.classList.remove("border-l-4", "border-l-blue-400")
            delete parentEl.dataset.hasDependents
          }
        }
      }
    } catch { /* silent */ }

    this._persistOrder()
  }

  _showFlash(message) {
    const container = document.getElementById("flash-messages")
    if (!container) return

    const flash = document.createElement("div")
    flash.className = "fixed inset-x-0 top-0 flex items-end justify-center mt-16 px-2 md:px-4 py-6 sm:p-6 z-90 pointer-events-none"
    flash.innerHTML = `
      <div class="max-w-sm w-full shadow-lg rounded-sm px-4 py-3 relative bg-yellow-400 border-l-4 border-orange-700 text-yellow-800 pointer-events-auto">
        <div class="p-2">
          <div class="flex items-start">
            <div class="ml-3 w-0 flex-1 pt-0.5">
              <p class="text-sm leading-5 font-medium">${message}</p>
            </div>
          </div>
        </div>
      </div>
    `
    container.appendChild(flash)
    setTimeout(() => flash.remove(), 4000)
  }

  // --- Reorder persistence ---

  _persistOrder() {
    const cards = this.element.querySelectorAll("[data-card-id]")
    const cardIds = Array.from(cards).map(el => el.dataset.cardId).filter(Boolean)
    if (cardIds.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ card_ids: cardIds })
    })
  }
}
